/**
 * ContractOutputEditorRightPanel.js
 *
 * Right sidebar for the contract output editor.
 * Shows:
 * - Selected object properties (for editing)
 * - Add freeform text button
 * - Add checkbox button
 * - Page management (reorder/delete)
 * - Warnings/issues list
 * - Zoom controls
 */

import React, { useState } from 'react';
import { COLORS, TYPOGRAPHY, SPACING, RADIUS } from '../designSystem';

export default function ContractOutputEditorRightPanel({
  selectedObject,
  onUpdateObject,
  onDeleteObject,
  onDuplicateObject,
  onAddFreeformText,
  onAddCheckbox,
  warnings,
  zoom,
  onZoomChange,
}) {
  const [freeformText, setFreeformText] = useState('');
  const [checkboxLabel, setCheckboxLabel] = useState('');

  if (!selectedObject) {
    return (
      <div style={styles.panel}>
        <div style={styles.section}>
          <h3 style={TYPOGRAPHY.H5}>Add Content</h3>
          <button
            style={styles.btn}
            onClick={() => {
              onAddFreeformText(freeformText || 'New text');
              setFreeformText('');
            }}
          >
            + Add Freeform Text
          </button>
          <input
            type="text"
            placeholder="Text content..."
            value={freeformText}
            onChange={(e) => setFreeformText(e.target.value)}
            style={styles.input}
          />

          <button
            style={styles.btn}
            onClick={() => {
              onAddCheckbox(checkboxLabel || 'New checkbox');
              setCheckboxLabel('');
            }}
          >
            + Add Checkbox
          </button>
          <input
            type="text"
            placeholder="Label..."
            value={checkboxLabel}
            onChange={(e) => setCheckboxLabel(e.target.value)}
            style={styles.input}
          />
        </div>

        <div style={styles.section}>
          <h3 style={TYPOGRAPHY.H5}>View Controls</h3>
          <div style={styles.zoomControl}>
            <button onClick={() => onZoomChange(zoom - 0.1)}>−</button>
            <span>{Math.round(zoom * 100)}%</span>
            <button onClick={() => onZoomChange(zoom + 0.1)}>+</button>
          </div>
        </div>

        {warnings && warnings.length > 0 && (
          <div style={styles.section}>
            <h3 style={TYPOGRAPHY.H5}>Issues</h3>
            {warnings.map((w, i) => (
              <div key={i} style={styles.warning}>{w}</div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={styles.panel}>
      <div style={styles.section}>
        <h3 style={TYPOGRAPHY.H5}>
          {selectedObject.object_type === 'text_field' ? 'Text Field' :
           selectedObject.object_type === 'freeform_text' ? 'Freeform Text' :
           'Checkbox'}
        </h3>

        {/* Value field (for text objects) */}
        {(selectedObject.object_type === 'text_field' || selectedObject.object_type === 'freeform_text') && (
          <>
            <label style={styles.label}>Value</label>
            <textarea
              value={selectedObject.value || ''}
              onChange={(e) => onUpdateObject(selectedObject.object_id, { value: e.target.value })}
              style={styles.textarea}
              rows="4"
            />
          </>
        )}

        {/* Checked field (for checkboxes) */}
        {selectedObject.object_type === 'checkbox' && (
          <>
            <label style={styles.label}>
              <input
                type="checkbox"
                checked={selectedObject.checked || false}
                onChange={(e) => onUpdateObject(selectedObject.object_id, { checked: e.target.checked })}
              />
              Checked
            </label>
            <label style={styles.label}>Label</label>
            <input
              type="text"
              value={selectedObject.label || ''}
              onChange={(e) => onUpdateObject(selectedObject.object_id, { label: e.target.value })}
              style={styles.input}
            />
          </>
        )}

        {/* Position and size */}
        <label style={styles.label}>X Position</label>
        <input
          type="number"
          value={selectedObject.x || 0}
          onChange={(e) => onUpdateObject(selectedObject.object_id, { x: parseFloat(e.target.value) })}
          style={styles.input}
        />

        <label style={styles.label}>Y Position</label>
        <input
          type="number"
          value={selectedObject.y || 0}
          onChange={(e) => onUpdateObject(selectedObject.object_id, { y: parseFloat(e.target.value) })}
          style={styles.input}
        />

        <label style={styles.label}>Width</label>
        <input
          type="number"
          value={selectedObject.width || 0}
          onChange={(e) => onUpdateObject(selectedObject.object_id, { width: parseFloat(e.target.value) })}
          style={styles.input}
        />

        <label style={styles.label}>Height</label>
        <input
          type="number"
          value={selectedObject.height || 0}
          onChange={(e) => onUpdateObject(selectedObject.object_id, { height: parseFloat(e.target.value) })}
          style={styles.input}
        />

        {/* Font size */}
        {(selectedObject.object_type === 'text_field' || selectedObject.object_type === 'freeform_text') && (
          <>
            <label style={styles.label}>Font Size</label>
            <input
              type="number"
              value={selectedObject.font_size || 12}
              onChange={(e) => onUpdateObject(selectedObject.object_id, { font_size: parseInt(e.target.value) })}
              style={styles.input}
            />
          </>
        )}

        {/* Include checkbox */}
        <label style={styles.label}>
          <input
            type="checkbox"
            checked={selectedObject.include !== false}
            onChange={(e) => onUpdateObject(selectedObject.object_id, { include: e.target.checked })}
          />
          Include in export
        </label>

        {/* Actions */}
        <div style={styles.actions}>
          <button style={styles.btnDanger} onClick={() => onDeleteObject(selectedObject.object_id)}>
            Delete
          </button>
          <button style={styles.btn} onClick={() => onDuplicateObject(selectedObject.object_id)}>
            Duplicate
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  panel: {
    width: 300,
    backgroundColor: COLORS.BG_SURFACE_SECONDARY,
    borderLeft: `1px solid ${COLORS.BORDER_DEFAULT}`,
    padding: SPACING.MD,
    overflowY: 'auto',
  },
  section: {
    marginBottom: SPACING.LG,
  },
  label: {
    display: 'block',
    marginBottom: SPACING.XS,
    fontSize: TYPOGRAPHY.LABEL.fontSize,
    fontWeight: 'bold',
    color: COLORS.TEXT_PRIMARY,
  },
  input: {
    width: '100%',
    padding: SPACING.XS,
    marginBottom: SPACING.SM,
    border: `1px solid ${COLORS.BORDER_DEFAULT}`,
    borderRadius: RADIUS.SM,
    fontSize: TYPOGRAPHY.BODY.fontSize,
  },
  textarea: {
    width: '100%',
    padding: SPACING.XS,
    marginBottom: SPACING.SM,
    border: `1px solid ${COLORS.BORDER_DEFAULT}`,
    borderRadius: RADIUS.SM,
    fontSize: TYPOGRAPHY.BODY.fontSize,
    fontFamily: 'monospace',
  },
  btn: {
    width: '100%',
    padding: SPACING.SM,
    marginBottom: SPACING.SM,
    backgroundColor: COLORS.PRIMARY,
    color: 'white',
    border: 'none',
    borderRadius: RADIUS.MD,
    cursor: 'pointer',
    fontSize: TYPOGRAPHY.BODY.fontSize,
  },
  btnDanger: {
    flex: 1,
    padding: SPACING.SM,
    backgroundColor: COLORS.ERROR,
    color: 'white',
    border: 'none',
    borderRadius: RADIUS.MD,
    cursor: 'pointer',
    fontSize: TYPOGRAPHY.BODY.fontSize,
  },
  actions: {
    display: 'flex',
    gap: SPACING.SM,
    marginTop: SPACING.MD,
  },
  warning: {
    padding: SPACING.SM,
    marginBottom: SPACING.SM,
    backgroundColor: '#fee2e2',
    borderLeft: `3px solid ${COLORS.ERROR}`,
    fontSize: TYPOGRAPHY.CAPTION.fontSize,
    color: COLORS.TEXT_PRIMARY,
  },
  zoomControl: {
    display: 'flex',
    gap: SPACING.SM,
    alignItems: 'center',
  },
};
